<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue';
import { useUxData } from "@/stores/ux-data";
import { useCharacterDataStore } from "@/stores/character-data";
import { useGuidedSetupStore } from "@/stores/guided-setup-store";
import { useAuthDataStore } from "@/stores/auth-data";
import { useGuidedElementPicker } from "@/composables/useGuidedElementPicker";
import ElementBrowser from "@/components/atoms/swade/ElementBrowser.vue";
import GuidedScopeFilters from "@/components/atoms/element-filters/GuidedScopeFilters.vue";
import FilterControl from "@/components/atoms/element-filters/FilterControl.vue";
import HindranceSeverityFilter from "@/components/atoms/element-filters/HindranceSeverityFilter.vue";
import { buildSecondaryChips, clearSecondaryChip } from "@/composables/useElementPredicates";
import { hindranceSeverityLabel } from '@/appdata/element-label-formatters';
import { resolveDrawbackType } from '@/appdata/container-fields';
import { containerDisplayNameFor } from '@/appdata/chosen-labels';
import { useTrappingEntries } from '@/composables/useTrappingEntries';
import TrappingEntryField from '@/components/atoms/swade/TrappingEntryField.vue';
import PerkPointAllocator from "@/components/swade/modals/PerkPointAllocator.vue";
import type { SwadeCharacterInterface, CharacterSheetInterface } from "@/types/characters";
import type { SwadeHindranceInterface, SwadeDrawbackInterface, SettingElementInterface } from "@/types/items";
import type { ColumnConfig, FilterChip } from "@/types/browser";

const props = defineProps<{ stepIndex: number; }>();
const character = defineModel<SwadeCharacterInterface | null>('character');

const ux = useUxData();
const auth = useAuthDataStore();
const characterData = useCharacterDataStore();
const setupStore = useGuidedSetupStore();

const perkPointAllocatorRef = ref<InstanceType<typeof PerkPointAllocator> | null>(null);

const {
  browserRef, open, onSelect,
  dataItems, selectedElement, loading, metadata, links,
  onSearch, onNextPage, onPreviousPage, filters,
  showWorldFilter, filtersActive, clearFilters,
} = useGuidedElementPicker('hindrances', {
  alias: 'disadvantages',
  character: () => character.value,
});

const hindrances = computed(() => character.value?.hindrances ?? []);
// A hindrance whose trapping modification leaves the value to the player shows TrappingEntryField
// on its card (spec 2026-10-05). The field needs no handler here: the store write rebuilds the
// overrides itself, and a trapping changes no perk or build number.
const { entryFor: trappingEntryFor } = useTrappingEntries(() => character.value, 'container.hindrances');
const perkPointsEarned = computed(() => characterData.build?.perkPoints?.total ?? 0);
const maxPerkPoints = computed(() => (characterData.build?.perkPoints?.max ?? 4));

watch(() => true, () => { setupStore.validateStep(props.stepIndex, true); }, { immediate: true });

const isForcedHindranceUuid = (uuid?: string): boolean => {
  if (!uuid) return false;
  return character.value?.protoData?.hindrances?.some((proto: any) => proto.uuid === uuid) ?? false;
};
// Widened to SettingElementInterface to match ElementBrowser's isAdded/canAdd prop types; every
// row this picker can show or add is a hindrance (see onAdd below and isHindrance's #details guard).
const isHindranceAdded = (h: SettingElementInterface): boolean =>
  hindrances.value.some((d) => d.hindrance?.uuid === h.uuid);
const isAdded = (h: SettingElementInterface) => isHindranceAdded(h);
const canAdd = (h: SettingElementInterface) => !isHindranceAdded(h) && !isForcedHindranceUuid(h.uuid);

// The picker only ever offers hindrances (useGuidedElementPicker('hindrances', ...) fetches the
// disadvantages collection); ElementBrowser's #details slot types its element generically. Same
// truthiness the v-if already tested.
const isHindrance = (element: SettingElementInterface | null | undefined): element is SwadeHindranceInterface => !!element;

const getSeverityLabel = (el: any): string =>
    hindranceSeverityLabel(el, ux.labels?.swade?.data?.hindrances?.types ?? {});

const columns = computed<ColumnConfig[]>(() => [
  { field: 'name', header: ux.labels?.swade?.data?.hindrances?.name ?? 'Name',
    columnClass: ['name-column'] },
  { field: 'type', header: ux.labels?.swade?.data?.hindrances?.category ?? 'Severity', formatter: (el: any) => getSeverityLabel(el) },
  { field: 'source.name', header: ux.labels?.swade?.data?.hindrances?.source ?? 'Source', formatter: (el: any) => el.source?.name ?? '' },
]);

const severity = computed<string[]>({
  get: () => (filters.value as any).severity ?? [],
  set: (value) => { (filters.value as any).severity = value; },
});

// Chips live with their controls: SourceFilterControl owns the source chip, and the
// severity chip comes from the shared per-type mapping the data lists and the world
// browser read (it drops the ['choice', type] sentinel's first element for display).
const severityChips = computed<FilterChip[]>(() => buildSecondaryChips('hindrances', filters.value as any));
const removeSeverity = (chip: FilterChip) => clearSecondaryChip('hindrances', filters.value as any, chip);

const onAdd = async (hindrance: SettingElementInterface) => {
  if (!character.value) return;
  await characterData.addDrawbackToCharacter(character.value, hindrance as SwadeHindranceInterface);
  await characterData.recalculateTotalPerkPoints(character.value);
  characterData.updateBuildStatus(character.value);
};
const onRemoveHindrance = async (drawback: SwadeDrawbackInterface) => {
  if (!character.value) return;
  await characterData.removeDrawback(character.value, drawback);
  await characterData.recalculateTotalPerkPoints(character.value);
  characterData.updateBuildStatus(character.value);
};
const onRemoveSelectedHindrance = () => {
  if (!selectedElement.value || !character.value) return;
  const drawback = hindrances.value.find((d) => d.hindrance?.uuid === selectedElement.value?.uuid);
  if (drawback) onRemoveHindrance(drawback);
};
const onHindranceTypeChanged = async (drawback: SwadeDrawbackInterface, newType: string) => {
  drawback.type = newType as 'major' | 'minor';
  if (character.value) {
    await characterData.recalculateTotalPerkPoints(character.value);
    characterData.updateBuildStatus(character.value);
  }
};
const onAllocatePerkPointsClick = () => { perkPointAllocatorRef.value?.show(); };

onMounted(() => {
  setupStore.validateStep(props.stepIndex, true);
  characterData.updateBuildValidation(character.value, characterData.buildOptions);
});
</script>

<template>
  <div class="character-hindrances-step">
    <div class="form-group mb-3">
      <label class="form-label fw-semibold" v-html="ux.labels?.guidedSetup?.character?.hindrancesLabel"></label>
      <div class="d-flex flex-fill">
        <span class="icon me-1 mb-auto"><span class="fa-light fa-info-circle"></span></span>
        <p class="form-text my-auto mb-2" v-html="ux.labels?.guidedSetup?.character?.hindrancesHelp"></p>
      </div>

      <div class="perk-points-tracker alert mb-3"
           :class="perkPointsEarned > maxPerkPoints ? 'alert-danger text-danger-subtle' : 'alert-info'">
        <div class="d-flex justify-content-between align-items-center">
          <span>
            <span class="fa-light fa-star me-2"></span>
            <span v-html="ux.labels?.guidedSetup?.character?.perkPoints"></span>
          </span>
          <span class="badge fs-6" :class="perkPointsEarned > maxPerkPoints ? 'bg-danger' : 'bg-primary'">{{ perkPointsEarned }} / {{ maxPerkPoints }}</span>
        </div>
        <small v-if="!(character as SwadeCharacterInterface).ancestry">
          <span class="fa-light fa-info-circle"></span>
          {{ ux.labels?.guidedSetup?.character?.perkPointsNoAncestry }}
        </small>
      </div>

      <div v-if="perkPointsEarned > 0" class="mb-3">
        <button type="button" class="btn btn-sm btn-outline-primary w-100" @click="onAllocatePerkPointsClick">
          <span class="fa-light fa-gear"></span>
          {{ ux.labels?.guidedSetup?.character?.allocatePerkPoints ?? 'Allocate Hindrance Points' }}
        </button>
      </div>

      <div v-if="hindrances.length > 0" class="hindrance-list d-flex flex-md-row flex-column flex-fill flex-wrap gap-2 mb-3">
        <!-- :key is required, not hygiene: keyed, a card's TrappingEntryField moves with it when
             the list reorders; unkeyed, Vue would hand the instance another card's props and a
             half-typed answer would be dropped. -->
        <div v-for="drawback in hindrances" :key="drawback.uuid" class="card card-body hindrance-item">
          <div class="d-flex justify-content-between align-items-center">
            <span class="d-flex align-items-center gap-2">
              <span class="hindrance-title fw-semibold">
                <!-- Unlike Hindrances.vue, this step has no separate view/edit mode — every
                     hindrance shown here is being built, so it branches directly on the CATALOG
                     type instead of a mode flag. Choice-type: the <select> right below is the
                     severity's sole owner, so the label stays atomic (raw catalog name, no
                     parenthetical) to avoid rendering it twice. The trapping entry field below owns
                     the trapping the same way (spec 2026-10-05), so a row showing it stays atomic
                     too. Neither control (fixed severity, no field): the label takes the full
                     composed, override-aware name from containerDisplayName — whose own severity
                     gate resolves to null for it anyway, since a fixed severity was never chosen.
                     Same rule as Hindrances.vue, reached by a direct type check rather than a mode
                     flag. See the consumer matrix in
                     docs/superpowers/specs/2026-09-07-container-display-name-parity-design.md -->
                {{ drawback.hindrance?.type === 'choice' || trappingEntryFor(drawback) !== null ? drawback.hindrance?.name : containerDisplayNameFor(character, drawback) }}
              </span>
              <span class="hindrance-type">
                <select
                  v-if="drawback.hindrance?.type === 'choice'"
                  :value="resolveDrawbackType(drawback)"
                  class="form-select form-select-sm w-auto"
                  @change="onHindranceTypeChanged(drawback, ($event.target as HTMLSelectElement).value)"
                >
                  <option value="minor">{{ ux.labels?.swade?.data?.hindrances?.types?.minor ?? 'Minor' }}</option>
                  <option value="major">{{ ux.labels?.swade?.data?.hindrances?.types?.major ?? 'Major' }}</option>
                </select>
              </span>
            </span>
            <button
              v-if="!isForcedHindranceUuid(drawback.hindrance?.uuid)"
              type="button"
              class="btn btn-sm btn-link text-danger"
              @click="onRemoveHindrance(drawback)"
            >
              <span class="fa-light fa-times"></span>
            </button>
          </div>
          <!-- On its own row and full width, unlike the sheet's inline 12rem field: a card here is a
               fifth of the list's width at lg, narrower than the field. d-flex and w-100 are
               !important utilities, so they win over the .trapping-entry rules. -->
          <div v-if="trappingEntryFor(drawback)" class="trapping-entry d-flex my-2">
            <TrappingEntryField class="w-100"
                                data-test="hindrance-trapping-entry"
                                :character="character!"
                                :container="drawback"
                                :effect="trappingEntryFor(drawback)!"
            />
          </div>
          <span v-if="auth.user?.subscriber" class="hindrance-description" v-html="drawback.hindrance.summary"></span>
        </div>
      </div>

      <button type="button" class="btn btn-primary w-100 d-lg-none mb-3 add-trigger" @click="open()">
        <span class="fa-light fa-plus"></span>
        {{ ux.labels?.guidedSetup?.character?.addHindrances ?? 'Add Hindrances' }}
      </button>

      <ElementBrowser
        ref="browserRef"
        :inline="true"
        :title="ux.labels?.guidedSetup?.character?.addHindrances ?? 'Add Hindrances'"
        :data-items="dataItems"
        :columns="columns"
        :selected-element="selectedElement"
        :is-added="isAdded"
        :can-add="canAdd"
        :loading="loading"
        :metadata="metadata"
        :links="links"
        :filters-active="filtersActive"
        @add="onAdd"
        @select="onSelect"
        @search="onSearch"
        @next="onNextPage"
        @previous="onPreviousPage"
        @clear="clearFilters"
      >
        <template #filters>
          <GuidedScopeFilters :show-world="showWorldFilter" />
          <FilterControl
            :label="ux.labels?.swade?.data?.hindrances?.category ?? 'Severity'"
            :chips="severityChips"
            :multiple="false"
            @remove="removeSeverity"
          >
            <HindranceSeverityFilter
              v-model:severity="severity"
              :label="ux.labels?.swade?.data?.filters?.labels?.severity ?? 'Severity'"
              :placeholder="ux.labels?.swade?.data?.filters?.severity ?? 'Severity'"
            />
          </FilterControl>
        </template>

        <template #details="{ element }">
          <template v-if="isHindrance(element)">
            <span class="badge mb-2" :class="{
              'bg-danger': element.type === 'major',
              'bg-warning text-dark': element.type === 'minor',
              'bg-info': element.type === 'choice',
            }">{{ getSeverityLabel(element) }}</span>
            <div v-if="auth.user?.subscriber && element.summary" class="card-text small mb-3" v-html="element.summary"></div>
            <div v-else-if="element.description" class="card-text small mb-3" v-html="element.description"></div>
            <button
              v-if="isHindranceAdded(element) && !isForcedHindranceUuid(element.uuid)"
              type="button"
              class="btn btn-outline-danger btn-sm"
              @click="onRemoveSelectedHindrance"
              v-html="ux.labels?.guidedSetup?.character?.removeHindrance ?? 'Remove Hindrance'"
            ></button>
          </template>
        </template>
      </ElementBrowser>
    </div>

    <PerkPointAllocator ref="perkPointAllocatorRef" :character="(character as CharacterSheetInterface | undefined)" />
  </div>
</template>
